/**
 * APIから返却される DateTime 文字列（yyyy-MM-dd'T'HH:mm:ss）をフォーマットするユーティリティ関数群
 */

/**
 * DateTime文字列を日付のみ（yyyy/MM/dd）に変換する
 * @example formatDate("2025-03-15T10:30:00") => "2025/03/15"
 */
export const formatDate = (dateTime: string): string => {
  const date = new Date(dateTime);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}/${m}/${d}`;
};

/**
 * DateTime文字列を日時（yyyy/MM/dd HH:mm）に変換する
 * @example formatDateTime("2025-03-15T10:30:00") => "2025/03/15 10:30"
 */
export const formatDateTime = (dateTime: string): string => {
  const date = new Date(dateTime);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${h}:${min}`;
};

/**
 * DateTime文字列を日時秒付き（yyyy/MM/dd HH:mm:ss）に変換する
 * @example formatDateTimeWithSeconds("2025-03-15T10:30:45") => "2025/03/15 10:30:45"
 */
export const formatDateTimeWithSeconds = (dateTime: string): string => {
  const date = new Date(dateTime);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}/${m}/${d} ${h}:${min}:${s}`;
};

/**
 * DateTime文字列を和暦日付に変換する
 * @example formatDateJP("2025-03-15T10:30:00") => "2025年3月15日"
 */
export const formatDateJP = (dateTime: string): string => {
  const date = new Date(dateTime);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/** 未設定を表すセンチネル日付（yyyy-MM-dd）。チケットの終了予定日等で使用する。 */
export const SENTINEL_DATE = '9999-12-31';

/**
 * DateTime文字列がセンチネル（9999-12-31）または空か判定する。
 * @example isSentinelDate("9999-12-31T23:59:59") => true
 */
export const isSentinelDate = (dateTime: string | null | undefined): boolean => {
  if (!dateTime) return true;
  return dateTime.substring(0, 10) === SENTINEL_DATE;
};

/**
 * DateTime文字列を日付（yyyy/MM/dd）に変換する。
 * ただし 9999-12-31（未設定センチネル）・null・空文字の場合は指定の代替文字列を返す。
 * チケットの一覧・詳細・ガント・ダッシュボードの全画面で使用する。
 * @example formatDateOrDash("9999-12-31T23:59:59") => "未設定"
 * @example formatDateOrDash("2026/03/15T00:00:00") => "2026/03/15"
 */
export const formatDateOrDash = (
  dateTime: string | null | undefined,
  fallback = '未設定',
): string => {
  if (isSentinelDate(dateTime)) return fallback;
  return formatDate(dateTime as string);
};
